import { Link } from 'react-router-dom';

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative border-t border-stone-800/50">
      {/* Subtle top gradient */}
      <div className="absolute top-0 right-0 left-0 h-px bg-gradient-to-r from-transparent via-stone-700/30 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 py-16 md:px-8">
        <div className="grid gap-12 md:grid-cols-3 md:gap-8">
          {/* Brand column */}
          <div className="space-y-4">
            <Link to="/" className="inline-flex items-baseline">
              <span className="font-display text-earth-cream text-xl tracking-wide">
                CORE<span className="text-stone-600">ALPINE</span>
              </span>
              <span className="font-display text-forest-light ml-0.5 text-sm">.dev</span>
            </Link>
            <p className="font-body max-w-xs text-sm leading-relaxed text-stone-500">
              Building the future with precision craftsmanship and alpine-inspired engineering.
            </p>
          </div>

          {/* Links column */}
          <div className="space-y-4">
            <h3 className="font-body text-xs tracking-[0.2em] text-stone-400 uppercase">Explore</h3>
            <nav className="flex flex-col gap-3">
              <FooterLink href="#about">About</FooterLink>
            </nav>
          </div>

          {/* Sister sites column */}
          <div className="space-y-4">
            <h3 className="font-body text-xs tracking-[0.2em] text-stone-400 uppercase">
              Core Alpine
            </h3>
            <nav className="flex flex-col gap-3">
              <a
                href="https://corealpine.design"
                target="_blank"
                rel="noopener noreferrer"
                className="group hover:text-earth-cream flex items-center gap-2 text-stone-500 transition-colors"
              >
                <span className="font-body text-sm">corealpine</span>
                <span className="text-earth-sand font-display">.design</span>
                <svg
                  className="h-3 w-3 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
              <Link to="/" className="group text-earth-cream flex items-center gap-2">
                <span className="font-body text-sm">corealpine</span>
                <span className="text-forest-light font-display">.dev</span>
                <span className="font-body text-xs text-stone-600">(you are here)</span>
              </Link>
            </nav>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 border-t border-stone-800/30 pt-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="font-body text-xs text-stone-600">
              &copy; {currentYear} Core Alpine. All rights reserved.
            </p>

            {/* Decorative element */}
            <div className="flex items-center gap-4">
              <span className="h-px w-8 bg-stone-800" />
              <span className="font-display text-xs tracking-widest text-stone-700">
                CRAFTED WITH PRECISION
              </span>
              <span className="h-px w-8 bg-stone-800" />
            </div>
          </div>

          <p className="font-body mt-6 text-center text-xs text-stone-500">
            Site designed &amp; built by{' '}
            <a
              href="https://corealpine.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="font-display text-earth-cream hover:text-earth-sand transition-colors"
            >
              corealpine.dev
            </a>
          </p>
        </div>
      </div>

      {/* Corner accent */}
      <div className="absolute right-4 bottom-4 opacity-20">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path
            d="M20 0L40 20L20 40L0 20L20 0Z"
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-stone-600"
          />
          <path
            d="M20 8L32 20L20 32L8 20L20 8Z"
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-forest"
          />
        </svg>
      </div>
    </footer>
  );
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="font-body hover:text-earth-cream inline-block transform text-sm text-stone-500 transition-colors duration-300 hover:translate-x-1"
    >
      {children}
    </a>
  );
}
